/**
 * Enhanced Phone Number Utilities for Frontend
 * Integrates with backend phone-number.util.ts for consistency
 * Optimized
 */

import api from "../services/api";
import { getApiUrl } from "../services/config";

export interface PhoneNumberValidationResult {
  isValid: boolean;
  sanitized?: string;
  error?: string;
}

/**
 * Frontend-only: Quick format phone number for display (client-side)
 * Matches backend formatPhoneNumberForDisplay behavior
 * For real-time UI formatting without API calls
 */
export function formatPhoneNumberDisplay(phoneNumber: string): string {
  if (!phoneNumber) return "";

  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, "");

  // Handle different lengths of US phone numbers
  if (cleaned.length === 0) {
    return "";
  } else if (cleaned.length === 11 && cleaned.startsWith("1")) {
    // Handle US numbers with country code (1XXXXXXXXXX) -> (XXX) XXX-XXXX
    const withoutCountryCode = cleaned.slice(1);
    return `(${withoutCountryCode.slice(0, 3)}) ${withoutCountryCode.slice(3, 6)}-${withoutCountryCode.slice(6)}`;
  } else if (cleaned.length <= 3) {
    return `(${cleaned}`;
  } else if (cleaned.length <= 6) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  } else if (cleaned.length <= 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else {
    // If more than 10 digits, take the last 10 digits (in case of country code)
    const lastTen = cleaned.slice(-10);
    return `(${lastTen.slice(0, 3)}) ${lastTen.slice(3, 6)}-${lastTen.slice(6)}`;
  }
}

/**
 * Convert phone number to E.164 format for backend storage
 * Matches backend sanitizePhoneNumber behavior
 */
export function toE164Format(phoneNumber: string): string {
  if (!phoneNumber) {
    throw new Error('Phone number is required');
  }

  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, "");

  // US-focused phone number handling (matches backend logic)
  if (cleaned.length === 10) {
    // Standard US 10-digit number - add US country code
    return '+1' + cleaned;
  }
  
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    // US number with country code already included
    return '+' + cleaned;
  }

  // For numbers that don't fit US patterns, try to make them US
  if (cleaned.length >= 10 && cleaned.length <= 11) {
    // Take last 10 digits for US format
    return '+1' + cleaned.slice(-10);
  }

  // For any other format, throw an error since this is US-focused
  throw new Error(
    `Invalid US phone number format: ${phoneNumber}. Expected 10-digit US number.`,
  );
}

/**
 * Extract raw 10-digit phone number from any format
 * Legacy function for backward compatibility
 */
export function extractPhoneNumber(formattedPhone: string): string {
  const cleaned = formattedPhone.replace(/\D/g, "");
  
  // If it's 11 digits and starts with 1, remove the country code
  if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return cleaned.slice(1);
  }
  
  // Return only the last 10 digits (in case country code was included)
  return cleaned.slice(-10);
}

/**
 * Enhanced US phone number validation (matches backend logic)
 * Validates area code starts with 2-9 (not 0 or 1)
 */
export function isValidUSPhoneNumber(phoneNumber: string): boolean {
  try {
    const e164 = toE164Format(phoneNumber);
    // Must be US format and have valid area code (2-9 for first digit)
    return e164.startsWith('+1') && /^\+1[2-9]\d{9}$/.test(e164);
  } catch (error) {
    return false;
  }
}

/**
 * Get area code from US phone number
 */
export function getUSAreaCode(phoneNumber: string): string | null {
  try {
    const e164 = toE164Format(phoneNumber);
    if (e164.startsWith('+1') && e164.length === 12) {
      return e164.substring(2, 5); // Extract 3 digits after +1
    }
    return null;
  } catch (error) {
    return null;
  }
}

/**
 * Compare two phone numbers after normalization
 */
export function arePhoneNumbersEqual(phone1: string, phone2: string): boolean {
  try {
    const e164_1 = toE164Format(phone1);
    const e164_2 = toE164Format(phone2);
    return e164_1 === e164_2;
  } catch (error) {
    return false;
  }
}

/**
 * Backend API Integration Functions
 * These call the actual backend utilities for server-side validation
 */

/**
 * Validate phone number using backend API
 * Calls backend /api/utils/phone/validate endpoint
 */
export async function validatePhoneNumberAPI(phoneNumber: string): Promise<PhoneNumberValidationResult> {
  try {
    const response = await api.post<{
      statusCode: number;
      data: PhoneNumberValidationResult;
    }>({
      url: getApiUrl('/api/utils/phone/validate'),
      body: { phoneNumber },
      auth: true,
    });

    if (response.data?.statusCode === 200) {
      return response.data.data;
    }

    return {
      isValid: false,
      error: 'Phone validation service error'
    };
  } catch (error) {

    return {
      isValid: false,
      error: 'Phone validation service unavailable'
    };
  }
}

/**
 * Sanitize phone number using backend API
 * Calls backend /api/utils/phone/sanitize endpoint
 */
export async function sanitizePhoneNumberAPI(phoneNumber: string): Promise<string> {
  try {
    const response = await api.post<{
      statusCode: number;
      data: { sanitized: string };
    }>({
      url: getApiUrl('/api/utils/phone/sanitize'),
      body: { phoneNumber },
      auth: true,
    });

    if (response.data?.statusCode === 200) {
      return response.data.data.sanitized;
    }

    throw new Error('Phone sanitization service error');
  } catch (error) {

    // Fallback to client-side logic
    return toE164Format(phoneNumber);
  }
}

/**
 * Format phone number using backend API
 * Calls backend /api/utils/phone/format endpoint
 */
export async function formatPhoneNumberAPI(phoneNumber: string): Promise<string> {
  try {
    const response = await api.post<{
      statusCode: number;
      data: { formatted: string };
    }>({
      url: getApiUrl('/api/utils/phone/format'),
      body: { phoneNumber },
      auth: true,
    });

    if (response.data?.statusCode === 200) {
      return response.data.data.formatted;
    }

    throw new Error('Phone formatting service error');
  } catch (error) {

    // Fallback to client-side logic
    return formatPhoneNumberDisplay(phoneNumber);
  }
}

/**
 * Legacy compatibility exports
 * These maintain backward compatibility with existing frontend code
 */
export const formatPhoneNumber = formatPhoneNumberDisplay;

/**
 * Phone validation regex patterns
 */
export const US_PHONE_REGEX = /^\(\d{3}\) \d{3}-\d{4}$/;
export const RAW_PHONE_REGEX = /^\d{10}$/;
export const E164_PHONE_REGEX = /^\+1[2-9]\d{9}$/;